import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Collapse,
  Container,
  Typography,
  IconButton,
  Stack,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Paper,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useAuth } from "../contexts/AuthContext";
import { useOffline } from "../contexts/OfflineContext";
import { apiGet, apiPost, apiPut, apiDelete } from "../utils/apiClient";
import { HEADER_GRADIENT, HEADER_TEXT } from "../App";

const CATEGORIES = ["food", "transport", "accommodation", "activities", "other"];

// Pure helper — module scope, never a hook dependency
const familySize = (fam) =>
  (fam.members || []).length + (fam.guests || []).length;

/**
 * Distribute `amount` among `fams` proportionally by headcount,
 * only for families whose id is in `selectedIds`.
 * Families not in selectedIds get "0.00".
 * Returns null when nothing to distribute.
 */
function recalcSplits(selectedIds, amount, fams) {
  if (!amount || fams.length === 0) return null;
  const total = parseFloat(amount);
  if (isNaN(total) || total <= 0) return null;

  const checked = fams.filter((f) => selectedIds.has(f.id));
  if (checked.length === 0) return null;

  const totalPeople = checked.reduce((s, f) => s + familySize(f), 0);

  let newSplits;
  if (totalPeople === 0) {
    // No people assigned yet → equal share among checked families
    const per = (total / checked.length).toFixed(2);
    newSplits = fams.map((fam) => ({
      family_id: fam.id,
      amount: selectedIds.has(fam.id) ? per : "0.00",
    }));
  } else {
    const perPerson = total / totalPeople;
    newSplits = fams.map((fam) => ({
      family_id: fam.id,
      amount: selectedIds.has(fam.id)
        ? (perPerson * familySize(fam)).toFixed(2)
        : "0.00",
    }));
    // Fix floating-point rounding: add remainder to the largest checked family
    const assignedSum = newSplits.reduce((s, x) => s + parseFloat(x.amount), 0);
    const remainder = Math.round((total - assignedSum) * 100) / 100;
    if (remainder !== 0) {
      const largestIdx = newSplits.reduce(
        (best, s, i) =>
          parseFloat(s.amount) >= parseFloat(newSplits[best].amount) ? i : best,
        0
      );
      newSplits[largestIdx] = {
        ...newSplits[largestIdx],
        amount: (parseFloat(newSplits[largestIdx].amount) + remainder).toFixed(2),
      };
    }
  }
  return newSplits;
}

export default function ExpenseForm() {
  const { tripId, expenseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshCount } = useOffline();
  const isEdit = Boolean(expenseId);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [trip, setTrip] = useState(null);
  const [families, setFamilies] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedFamilyIds, setSelectedFamilyIds] = useState(new Set());
  const [equalOpen, setEqualOpen] = useState(false);
  const [form, setForm] = useState({
    paid_by_user_id: user?.id || "",
    paid_by_guest_id: "",
    amount: "",
    description: "",
    category: "other",
    date: new Date().toISOString().split("T")[0],
    split_type: "equal",
    splits: [],
  });

  // ── Load trip data ────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      const [tripData, famData] = await Promise.all([
        apiGet(`/api/trips/${tripId}`),
        apiGet(`/api/trips/${tripId}/families`),
      ]);
      setTrip(tripData);
      setFamilies(famData);
      setMembers(tripData.members?.filter((m) => m.status === "approved") || []);

      if (isEdit) {
        const expData = await apiGet(`/api/trips/${tripId}/expenses`);
        const exp = expData.find((e) => e.id === parseInt(expenseId));
        if (exp) {
          const loadedSplits = exp.splits || [];
          setForm({
            paid_by_user_id: exp.paid_by_guest_id ? "" : exp.paid_by_user_id,
            paid_by_guest_id: exp.paid_by_guest_id || "",
            amount: exp.amount,
            description: exp.description,
            category: exp.category || "other",
            date: exp.date?.split("T")[0] || new Date().toISOString().split("T")[0],
            split_type: "custom",
            splits: loadedSplits,
          });
          // Restore selection: families with amount > 0 are selected;
          // families missing from splits default to selected.
          const splitMap = Object.fromEntries(
            loadedSplits.map((s) => [s.family_id, parseFloat(s.amount)])
          );
          setSelectedFamilyIds(
            new Set(
              famData
                .filter((f) => !(f.id in splitMap) || splitMap[f.id] > 0)
                .map((f) => f.id)
            )
          );
          return; // skip default selection init below
        }
      }
      // Default: all families selected
      setSelectedFamilyIds(new Set(famData.map((f) => f.id)));
    };
    load().catch((err) => { console.error(err); setErrorMsg("Failed to load expense data. Please try again."); });
  }, [tripId, expenseId, isEdit]);

  // ── Equal mode: auto-recalc when amount / families / selection changes ────
  useEffect(() => {
    if (form.split_type !== "equal" || families.length === 0 || !form.amount) return;
    const newSplits = recalcSplits(selectedFamilyIds, form.amount, families);
    if (newSplits) setForm((f) => ({ ...f, splits: newSplits }));
  }, [form.split_type, form.amount, families, selectedFamilyIds]);

  // ── Toggle a family in/out of the split ──────────────────────────────────
  const handleToggleFamily = (familyId) => {
    const next = new Set(selectedFamilyIds);
    if (next.has(familyId)) next.delete(familyId);
    else next.add(familyId);
    setSelectedFamilyIds(next);

    // Custom mode: recalc immediately (equal mode is handled by its useEffect)
    if (form.split_type === "custom" && form.amount) {
      const newSplits = recalcSplits(next, form.amount, families);
      if (newSplits) setForm((f) => ({ ...f, splits: newSplits }));
    }
  };

  // ── Manual split edit (custom mode only) ─────────────────────────────────
  const setSplitAmount = (familyId, value) => {
    setForm((f) => ({
      ...f,
      splits: f.splits.map((s) =>
        s.family_id === familyId ? { ...s, amount: value } : s
      ),
    }));
  };

  // ── Submit / Delete ───────────────────────────────────────────────────────
  const handleSubmit = async () => {
    try {
      const payload = { ...form };
      if (isEdit) {
        await apiPut(`/api/trips/${tripId}/expenses/${expenseId}`, payload);
      } else {
        await apiPost(`/api/trips/${tripId}/expenses`, payload);
      }
      await refreshCount();
      window.scrollTo(0, 0);
      navigate(`/trips/${tripId}`);
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to save expense. Please try again.");
    }
  };

  const handleDelete = async () => {
    setDeleteConfirm(false);
    await apiDelete(`/api/trips/${tripId}/expenses/${expenseId}`);
    await refreshCount();
    window.scrollTo(0, 0);
    await new Promise(resolve => setTimeout(resolve, 50));
    navigate(`/trips/${tripId}`);
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const currency  = trip?.currency || "EUR";
  const total     = parseFloat(form.amount) || 0;
  const splitSum  = form.splits.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const splitDiff = Math.round((total - splitSum) * 100) / 100;
  const splitsValid =
    form.split_type === "equal" || Math.abs(splitDiff) < 0.005;
  const valid =
    form.description.trim() && form.amount && (form.paid_by_user_id || form.paid_by_guest_id) && splitsValid;

  return (
    <Box sx={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* ── Header ── */}
      <Box sx={{ background: HEADER_GRADIENT, px: 1, py: 1, display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        <IconButton size="small" onClick={() => navigate(`/trips/${tripId}`)} sx={{ color: HEADER_TEXT }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1, color: HEADER_TEXT, textAlign: "center" }}>
          {isEdit ? "Edit Expense ✏️" : "New Expense ✨"}
        </Typography>
        {isEdit && (
          <IconButton size="small" onClick={() => setDeleteConfirm(true)} sx={{ color: HEADER_TEXT }}>
            <DeleteIcon />
          </IconButton>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", overscrollBehavior: "contain" }}>
      <Container maxWidth="sm" sx={{ py: 2.5 }}>
        <Stack spacing={2}>
          <TextField
            label="Description"
            fullWidth
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <TextField
            label={`Amount (${currency})`}
            type="number"
            fullWidth
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            inputProps={{ min: 0, step: "0.01" }}
          />

          <TextField
            select
            label="Paid by"
            fullWidth
            value={form.paid_by_guest_id ? `g_${form.paid_by_guest_id}` : `u_${form.paid_by_user_id}`}
            onChange={(e) => {
              const val = e.target.value;
              if (val.startsWith("g_")) {
                setForm({ ...form, paid_by_user_id: "", paid_by_guest_id: val.slice(2) });
              } else {
                setForm({ ...form, paid_by_user_id: val.slice(2), paid_by_guest_id: "" });
              }
            }}
          >
            {members.map((m) => (
              <MenuItem key={`u_${m.id}`} value={`u_${m.id}`}>{m.name}</MenuItem>
            ))}
            {families.flatMap((f) => (f.guests || []).map((g) => (
              <MenuItem key={`g_${g.id}`} value={`g_${g.id}`}>{g.name}</MenuItem>
            )))}
          </TextField>

          <TextField
            select
            label="Category"
            fullWidth
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          >
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Date"
            type="date"
            fullWidth
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />

          {/* ── Split type toggle ── */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="bold"
              sx={{ textTransform: "uppercase", letterSpacing: 1 }}
            >
              Split
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <ToggleButtonGroup
                value={form.split_type}
                exclusive
                onChange={(_, v) => v && setForm({ ...form, split_type: v })}
                size="small"
              >
                <ToggleButton value="equal">Equal</ToggleButton>
                <ToggleButton value="custom">Custom</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* ── No families hint ── */}
          {families.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              No families set up yet. Go to trip settings to add families and split expenses.
            </Typography>
          )}

          {/* ── EQUAL: collapsible, no checkboxes ── */}
          {form.split_type === "equal" && families.length > 0 && (() => {
            const totalPeople = families.reduce((s, f) => s + familySize(f), 0);
            const perPerson   = totalPeople > 0 && total > 0 ? total / totalPeople : 0;
            return (
              <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                {/* Collapsed summary header */}
                <Box
                  onClick={() => setEqualOpen((o) => !o)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Equal split
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {totalPeople > 0 && total > 0
                        ? `${perPerson.toFixed(2)} ${currency}/person · ${totalPeople} ${totalPeople === 1 ? "person" : "people"}`
                        : `${families.length} ${families.length === 1 ? "family" : "families"}`}
                    </Typography>
                  </Box>
                  {equalOpen ? <ExpandLessIcon fontSize="small" color="action" /> : <ExpandMoreIcon fontSize="small" color="action" />}
                </Box>

                {/* Expanded rows */}
                <Collapse in={equalOpen}>
                  <Stack spacing={0.5} sx={{ px: 2, pb: 2 }}>
                    {families.map((fam) => {
                      const size  = familySize(fam);
                      const split = form.splits.find((s) => s.family_id === fam.id);
                      return (
                        <Box
                          key={fam.id}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">{fam.name}</Typography>
                            {totalPeople > 0 && (
                              <Typography variant="caption" color="text.disabled">
                                {size} {size === 1 ? "person" : "people"}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="body2" fontWeight="bold">
                            {split?.amount ?? "–"} {currency}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Collapse>
              </Paper>
            );
          })()}

          {/* ── CUSTOM: editable fields with checkboxes + live validation ── */}
          {form.split_type === "custom" && families.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                {families.map((fam) => {
                  const checked    = selectedFamilyIds.has(fam.id);
                  const split      = form.splits.find((s) => s.family_id === fam.id);
                  const showAdjust = checked && !splitsValid;
                  return (
                    <Box
                      key={fam.id}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => handleToggleFamily(fam.id)}
                        sx={{ p: 0.5, flexShrink: 0 }}
                      />
                      <TextField
                        label={fam.name}
                        type="number"
                        size="small"
                        fullWidth
                        disabled={!checked}
                        value={checked ? (split?.amount || "") : "0.00"}
                        onChange={(e) => setSplitAmount(fam.id, e.target.value)}
                        inputProps={{ min: 0, step: "0.01" }}
                        InputProps={{
                          endAdornment: (
                            <Typography variant="caption" color="text.secondary">
                              {currency}
                            </Typography>
                          ),
                        }}
                      />
                      {showAdjust && (
                        <IconButton
                          size="small"
                          sx={{
                            flexShrink: 0,
                            p: 0.5,
                            color: splitDiff > 0 ? "success.main" : "error.main",
                          }}
                          onClick={() => {
                            const current = parseFloat(split?.amount || 0);
                            const adjusted = Math.max(0, current + splitDiff).toFixed(2);
                            setSplitAmount(fam.id, adjusted);
                          }}
                        >
                          {splitDiff > 0
                            ? <AddCircleOutlineIcon fontSize="small" />
                            : <RemoveCircleOutlineIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </Box>
                  );
                })}

                {/* Validation bar */}
                {total > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      pt: 1.5,
                      mt: 0.5,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Assigned / Total
                    </Typography>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={splitsValid ? "success.main" : "error.main"}
                      >
                        {splitSum.toFixed(2)} / {total.toFixed(2)} {currency}
                      </Typography>
                      {!splitsValid && (
                        <Typography variant="caption" color="error.main">
                          {splitDiff > 0
                            ? `${splitDiff.toFixed(2)} ${currency} missing`
                            : `${Math.abs(splitDiff).toFixed(2)} ${currency} too much`}
                        </Typography>
                      )}
                      {splitsValid && (
                        <Typography variant="caption" color="success.main">
                          ✓ Splits match total
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSubmit}
            disabled={!valid}
          >
            {isEdit ? "Save Changes" : "Add Expense"}
          </Button>
        </Stack>
      </Container>
      </Box>

      <Snackbar open={Boolean(errorMsg)} autoHideDuration={4000} onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Expense?</DialogTitle>
        <DialogContent>
          <Typography>This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteConfirm(false)} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
